import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Zap, Filter, Check } from "lucide-react";
import SubsidyHint from "@/components/SubsidyHint";
import { tr } from "@/lib/i18n";

/**
 * QuickstartPage, 1-klik kant-en-klare bundels.
 * Klant kiest een preset → addAll() vult de cart → /cart
 */
export default function QuickstartPage() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("");
  const [partner, setPartner] = useState("");
  const [sectors, setSectors] = useState([]);
  const [partners, setPartners] = useState([]);
  const { addToCart, lang } = useApp();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [pRes, sRes, prRes] = await Promise.all([
          api.get("/quickstart-presets"),
          api.get("/sectors"),
          api.get("/partners?landing_only=true").catch(() => ({ data: [] })),
        ]);
        setPresets(pRes.data || []);
        setSectors(sRes.data || []);
        setPartners(prRes.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = useMemo(() => {
    return presets
      .filter((p) => !sector || p.sector_slug === sector)
      .filter((p) => !partner || p.partner_slug === partner);
  }, [presets, sector, partner]);

  const startPreset = async (preset) => {
    try {
      const { data } = await api.get(`/quickstart-presets/${preset.slug}`);
      // Add hardware
      if (data.hardware) {
        addToCart({
          type: "hardware",
          ref_id: data.hardware.id,
          sku: data.hardware.sku,
          name: data.hardware.name,
          unit_price: data.hardware.purchase_price || 0,
          monthly_price: 0,
          pricing_mode: "buy",
        });
      }
      // Add software (subscription)
      (data.software || []).forEach((s) =>
        addToCart({
          type: "software",
          ref_id: s.id,
          sku: s.slug,
          name: s.name,
          unit_price: s.one_time_setup || 0,
          monthly_price: s.price_monthly || 0,
          pricing_mode: "subscription",
        })
      );
      // Add addons
      (data.addons || []).forEach((a) =>
        addToCart({
          type: "addon",
          ref_id: a.id,
          sku: a.sku,
          name: a.name,
          unit_price: a.price || 0,
          monthly_price: 0,
          pricing_mode: "buy",
        })
      );
      toast.success(`${tr(data.name_i18n, lang) || data.name_i18n?.nl} toegevoegd aan winkelwagen`);
      nav("/cart");
    } catch (e) {
      toast.error("Kon preset niet laden");
    }
  };

  return (
    <div className="min-h-screen pb-16" data-testid="quickstart-page">
      {/* Hero */}
      <section className="container-x pt-12 pb-8">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D97742] mb-3">
          <Zap className="w-3.5 h-3.5" /> Quickstart
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold text-white max-w-3xl leading-tight">
          Kant-en-klare bundels. Eén klik naar offerte.
        </h1>
        <p className="text-base text-[#A8B3AD] mt-4 max-w-2xl">
          Weet jij waarvoor je de kiosk gaat gebruiken, maar geen zin om alles los te configureren?
          Kies hieronder een logische bundel, hardware, software en addons staan al klaar. Pas je quote later in detail aan in de winkelwagen.
        </p>
      </section>

      {/* Filters */}
      <section className="container-x pb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-[#A8B3AD] mr-2 flex items-center gap-1">
            <Filter className="w-3 h-3" /> Filter:
          </span>
          <Chip active={!sector && !partner} onClick={() => { setSector(""); setPartner(""); }}>Alles</Chip>
          {sectors.slice(0, 6).map((s) => (
            <Chip key={s.slug} active={sector === s.slug} onClick={() => setSector(sector === s.slug ? "" : s.slug)}>
              {tr(s.name_i18n, lang) || s.name_i18n?.nl || s.slug}
            </Chip>
          ))}
          <span className="w-px h-4 bg-[#1F3A2E] mx-1" />
          {partners.slice(0, 6).map((p) => (
            <Chip key={p.slug} active={partner === p.slug} onClick={() => setPartner(partner === p.slug ? "" : p.slug)}>
              {p.name}
            </Chip>
          ))}
        </div>
      </section>

      {/* Cards */}
      <section className="container-x">
        {loading && <div className="text-center py-20 text-[#A8B3AD]">Laden…</div>}
        {!loading && visible.length === 0 && (
          <div className="text-center py-16 text-[#A8B3AD]">
            Geen presets gevonden met deze filter. <button onClick={() => { setSector(""); setPartner(""); }} className="underline text-[#D97742]">Reset filters</button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((p) => (
            <PresetCard key={p.id} preset={p} lang={lang} onStart={() => startPreset(p)} />
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="container-x mt-16">
        <div className="card-surface p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h3 className="text-xl font-semibold text-white">Liever zelf bouwen?</h3>
            <p className="text-sm text-[#A8B3AD] mt-1">Onze wizard helpt je in 4 stappen tot een persoonlijke offerte.</p>
          </div>
          <Link to="/configurator" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#D97742] text-white font-semibold hover:bg-[#c46939] transition" data-testid="quickstart-to-configurator">
            Naar de configurator <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${active ? "bg-[#D97742] border-[#D97742] text-white" : "border-[#1F3A2E] text-[#A8B3AD] hover:border-[#D97742]/50"}`}
    >
      {children}
    </button>
  );
}

function PresetCard({ preset, lang, onStart }) {
  const accent = preset.accent_color || "#D97742";
  const totalItems = 1 + (preset.software_ids?.length || 0) + (preset.addon_ids?.length || 0);
  return (
    <article
      className="card-surface overflow-hidden flex flex-col group hover:translate-y-[-2px] hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-300"
      data-testid={`preset-card-${preset.slug}`}
    >
      {/* Hero image */}
      <div className="relative h-44 bg-[#0F1816] overflow-hidden">
        {preset.hero_image ? (
          <img src={preset.hero_image} alt={tr(preset.name_i18n, lang) || preset.slug} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#5d6a64]">
            <Sparkles className="w-12 h-12 opacity-30" />
          </div>
        )}
        {preset.partner_slug && (
          <span
            className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white"
            style={{ backgroundColor: accent }}
          >
            {preset.partner_slug}
          </span>
        )}
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-mono text-white bg-black/40 backdrop-blur">
          {totalItems} items
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-semibold text-white leading-snug" style={{ color: accent }}>
          {tr(preset.name_i18n, lang) || preset.name_i18n?.nl || preset.slug}
        </h3>
        <p className="text-sm text-[#A8B3AD] mt-1.5 line-clamp-2">
          {tr(preset.tagline_i18n, lang) || preset.tagline_i18n?.nl}
        </p>
        <p className="text-xs text-[#A8B3AD]/80 mt-3 line-clamp-3 flex-1">
          {tr(preset.description_i18n, lang) || preset.description_i18n?.nl}
        </p>

        {/* Hardware summary */}
        {preset.hardware_name && (
          <div className="mt-3 px-3 py-2 rounded bg-[#0F1816] border border-[#1F3A2E] flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-[#A8B3AD]">Hardware</div>
              <div className="text-xs text-white truncate">{preset.hardware_name}</div>
            </div>
            {preset.hardware_price ? (
              <div className="text-right shrink-0">
                <div className="text-[10px] text-[#A8B3AD]">vanaf</div>
                <div className="text-sm font-semibold" style={{ color: accent }}>€{Math.round(preset.hardware_price).toLocaleString("nl-NL")}</div>
              </div>
            ) : null}
          </div>
        )}

        {/* Subsidy hint, uses total bundle price (hw + addons + sw setup) */}
        <SubsidyHint amount={preset.total_one_time_eur || preset.hardware_price} variant="card" />

        {/* Meta */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {preset.placement && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1F3A2E] text-[#7FD8B8] uppercase tracking-wider">
              {preset.placement === "indoor" ? "Binnen" : preset.placement === "outdoor" ? "Buiten" : "Indoor/Outdoor"}
            </span>
          )}
          {preset.sector_slug && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1F3A2E] text-[#A8B3AD]">
              {preset.sector_slug}
            </span>
          )}
        </div>

        <button
          onClick={onStart}
          data-testid={`preset-start-${preset.slug}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition group/btn"
          style={{ backgroundColor: accent }}
        >
          <Check className="w-4 h-4" /> Gebruik deze bundel
          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
        </button>
      </div>
    </article>
  );
}
